import { Alert, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AddEditButton from "src/reusable-components/AddEditButton/AddEditButton";
import { Props, State } from "./interfaces";
import { LinkExistingDocument } from "./LinkExistingDocument/LinkExistingDocument";
import { Button } from "./styles";
import { DocumentDTO } from "../../models/documents/document-dto";
import { TransactionDTO } from "../../models/transactions";
import WidgetButton from "../_Widgets/Widget/WidgetButton/WidgetButton";
import { DocumentsType } from "../AddTransaction/AddEditTransaction/interfaces";
import DialogWithConfirmationChangesDialog from "../DialogWithConfirmationChangesDialog/DialogWithConfirmationChangesDialog";
import Document from "../Document/Document/Document";
import { DocumentEvents } from "../Document/Document/utils";
import LastLoadedEntities from "../LastLoadedEntities/LastLoadedEntities";

const AddNewDocument = ({
  buttonType = "text",
  CustomButtonContent,
  linkStyles = false,
  variant = "text",
  openButtonKey,
  transaction,
  isWidget = false,
  withLastDocs = false,
  allowMarkVerified = false,
  handleNotifyParent,
  documentFieldsProps,
  documentName,
  isPredefinedDocName,
  relation,
  changeDateOrPeriodOnly = false,
  readOnlyFields,
  displayExpandableItemUponSave = true,
  forceClose,
  handleConfirm,
  filesProps,
  handleChangeDocumentToLink,
  documentToLink,
  hideAllowMarkVerified,
  customNameForDocToLink,
  showLinkDocument = false,
  plusButtonStyle,
  hideReadOnlyFields = false,
  maxFilesNumber,
  useMiniPlusButton = false,
  customWidgetButtonStyle,
  auditTypeIds = [],
  infoMessage,
  wrappedInDialog = true,
  customSaveTitle,
  closeAfterSave,
  onlyAcceptedExtensions,
  customSaveLogic,
  disabled,
  isGenericDocument,
  customLinkStyle,
}: Props) => {
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    isDocumentSaved: false,
    isDocumentMutated: false,
    document: null,
    forceCloseMainDialog: false,
    filesCount: 0,
    closeAfterSave: false,
  });

  //#region notify
  const handleNotify = (event: DocumentEvents, document: DocumentDTO | null, filesCount?: number) => {
    switch (event) {
      case DocumentEvents.DOCUMENT_IS_MUTATED:
        setState(() => ({
          ...state,
          isDocumentMutated: true,
          isDocumentSaved: false,
          document,
          filesCount: filesCount ?? 0,
        }));
        break;

      case DocumentEvents.DOCUMENT_IS_SAVED:
        setState(() => ({
          isDocumentMutated: false,
          isDocumentSaved: true,
          document,
          forceCloseMainDialog: displayExpandableItemUponSave ? false : true,
          filesCount: filesCount ?? 0,
          closeAfterSave: state.closeAfterSave,
        }));
        break;
    }
  };
  //#endregion

  //#region confirm and close
  const handleConfirmAndClose = (documents: DocumentsType) => {
    setState((state) => ({
      ...state,
      forceCloseMainDialog: !!documents.document,
    }));
    handleConfirm && handleConfirm(documents);
  };
  //#endregion

  //#region confirm link document and close
  const handleConfirmLinkDocumentAndClose = (document: DocumentDTO | null) => {
    setState((state) => ({
      ...state,
      forceCloseMainDialog: !!document,
    }));
    handleChangeDocumentToLink && handleChangeDocumentToLink(document);
  };
  //#endregion

  //#region reset force close main dialog
  const resetForceCloseMainDialog = () => {
    setState((state) => ({
      ...state,
      forceCloseMainDialog: false,
    }));
  };
  //#endregion

  //#region discard changes
  const handleDiscardChanges = (): void => setState((state) => ({ ...state, isDocumentMutated: false }));
  //#endregion

  useEffect(() => {
    if (state.isDocumentSaved && handleNotifyParent) {
      forceClose && forceClose();

      handleNotifyParent({ ...state.document } as DocumentDTO);

      // we reset state, because state.isDocumentSaved is a dependency so to avoid infinite 'notifyParent'
      setState((state) => ({
        ...state,
        isDocumentSaved: false,
        isDocumentMutated: false,
        closeAfterSave: !!closeAfterSave,
      }));
    }
  }, [state.isDocumentSaved, handleNotifyParent, state.document, forceClose]);

  return (
    <>
      {wrappedInDialog ? (
        <DialogWithConfirmationChangesDialog
          openButton={
            isWidget && buttonType !== "plusButton" && buttonType !== "commonButton" ? (
              <WidgetButton
                translationKey={openButtonKey ?? ""}
                customWidgetButtonStyle={customWidgetButtonStyle}
                disabled={disabled}
              />
            ) : buttonType === "plusButton" ? (
              <AddEditButton
                title={t(openButtonKey ?? "")}
                effect={"add"}
                customStyle={{ ...plusButtonStyle }}
                useMiniPlusButton={useMiniPlusButton}
                disabled={!!disabled}
              />
            ) : buttonType === "custom" ? (
              <IconButton disabled={!!disabled}>{CustomButtonContent}</IconButton>
            ) : (
              // <PlusButton
              //   variant="outlined"
              //   title={t(openButtonKey ?? "")}
              //   style={{...plusButtonStyle}}
              //   isMini={useMiniPlusButton}
              // >
              //   <FontAwesomeIcon icon={faPlus} />
              // </PlusButton>
              <Button
                variant={variant}
                linkStyles={linkStyles}
                disabled={!!disabled}
                style={customLinkStyle}
              >
                {`${t(openButtonKey ?? "")}`}
              </Button>
            )
          }
          title={t("components.attached_documents.adding_one")}
          hasChanges={state.isDocumentMutated}
          handleDiscardChanges={handleDiscardChanges}
          forceCloseMainDialog={state.forceCloseMainDialog}
          resetForceCloseMainDialog={resetForceCloseMainDialog}
          closeAfterSave={state.closeAfterSave}
          seCloseAfterSave={() => {
            setState((state) => ({
              ...state,
              closeAfterSave: false,
            }));
          }}
          disabled={disabled}
        >
          {infoMessage ? <Alert severity="info"> {infoMessage}</Alert> : <></>}
          <Document
            documentName={documentName}
            isPredefinedName={isPredefinedDocName}
            transaction={transaction}
            isWidget={isWidget}
            handleNotify={handleNotify}
            documentFieldsProps={documentFieldsProps}
            allowMarkVerified={allowMarkVerified}
            hideAllowMarkVerified={hideAllowMarkVerified}
            relation={relation}
            changeDateOrPeriodOnly={changeDateOrPeriodOnly}
            readOnlyFields={readOnlyFields}
            displayExpandableItemUponSave={displayExpandableItemUponSave}
            handleConfirm={handleConfirm ? handleConfirmAndClose : undefined}
            filesProps={filesProps}
            hideReadOnlyFields={hideReadOnlyFields}
            maxFilesNumber={maxFilesNumber}
            auditTypeIds={auditTypeIds}
            customSaveTitle={customSaveTitle}
            onlyAcceptedExtensions={onlyAcceptedExtensions}
            customSaveLogic={customSaveLogic}
            isGenericDocument={isGenericDocument}
          />

          {showLinkDocument && (
            <LinkExistingDocument
              transaction={transaction as TransactionDTO}
              documentToLink={documentToLink as DocumentDTO | null}
              handleChangeDocumentToLink={handleChangeDocumentToLink ? handleConfirmLinkDocumentAndClose : undefined}
              customNameForDocToLink={customNameForDocToLink}
              forceClose={forceClose}
              handleNotifyParent={handleNotifyParent as () => void}
            />
          )}

          {withLastDocs && <LastLoadedEntities />}
        </DialogWithConfirmationChangesDialog>
      ) : (
        <>
          {infoMessage ? <Alert severity="info"> {infoMessage}</Alert> : <></>}
          <Document
            documentName={documentName}
            isPredefinedName={isPredefinedDocName}
            transaction={transaction}
            isWidget={isWidget}
            handleNotify={handleNotify}
            documentFieldsProps={documentFieldsProps}
            allowMarkVerified={allowMarkVerified}
            hideAllowMarkVerified={hideAllowMarkVerified}
            relation={relation}
            changeDateOrPeriodOnly={changeDateOrPeriodOnly}
            readOnlyFields={readOnlyFields}
            displayExpandableItemUponSave={displayExpandableItemUponSave}
            handleConfirm={handleConfirm ? handleConfirmAndClose : undefined}
            filesProps={filesProps}
            hideReadOnlyFields={hideReadOnlyFields}
            maxFilesNumber={maxFilesNumber}
            auditTypeIds={auditTypeIds}
            customSaveTitle={customSaveTitle}
            onlyAcceptedExtensions={onlyAcceptedExtensions}
            customSaveLogic={customSaveLogic}
            isGenericDocument={isGenericDocument}
          />

          {showLinkDocument && (
            <LinkExistingDocument
              transaction={transaction as TransactionDTO}
              documentToLink={documentToLink as DocumentDTO | null}
              handleChangeDocumentToLink={handleChangeDocumentToLink ? handleConfirmLinkDocumentAndClose : undefined}
              customNameForDocToLink={customNameForDocToLink}
              forceClose={forceClose}
              handleNotifyParent={handleNotifyParent as () => void}
            />
          )}

          {withLastDocs && <LastLoadedEntities />}
        </>
      )}
    </>
  );
};

export default AddNewDocument;
